/**
 * Interface with the server for Account Settings.
 */
import type { ApolloClient } from '@apollo/client';
import URI from 'jsuri';
import _ from 'lodash';
import Q from 'q';

import API from 'js/lib/api';
import cookie from 'js/lib/cookie';
import user from 'js/lib/user';

import SettingsServerActionCreators from 'bundles/account-settings/actions/SettingsServerActionCreators';
import SettingsConstants from 'bundles/account-settings/constants/SettingsConstants';
import GetCoursesForEmailPreferencesQuery from 'bundles/account-settings/queries/GetCoursesForEmailPreferencesQuery.graphql';
import SettingsStore from 'bundles/account-settings/stores/SettingsStore';
import type { EmailPreference, NotificationPreferenceChannel } from 'bundles/account-settings/types/EmailPreferences';
import { NotificationPreferenceChannels } from 'bundles/account-settings/types/EmailPreferences';
import type { PaymentWallet } from 'bundles/account-settings/types/Payment';
import type PaymentWalletsV1 from 'bundles/naptimejs/resources/paymentWallets.v1';
import GetPaymentMethodsByPaymentProcessorIds from 'bundles/payments-common/api/GetPaymentMethodsByPaymentProcessorIds.graphql';
import type {
  GetPaymentMethodsByPaymentProcessorIdsQuery,
  GetPaymentMethodsByPaymentProcessorIdsQueryVariables,
  PaymentMethodDetailsFragment,
} from 'bundles/payments-common/api/__generated__/GetPaymentMethodsByPaymentProcessorIds';
import PaymentProcessorTypes from 'bundles/payments-common/common/PaymentProcessorTypes';
import { STRIPE_CONSUMER_PAYMENT_PROCESSOR_IDS } from 'bundles/payments-common/common/constants';
import { getActionUrl } from 'bundles/user-account/common/lib';

const { Sections, Fields, Subscriptions, PartnerBannerStatus, EMAIL_PREFERENCE_LOGO_SIZE } = SettingsConstants;

type NotificationPreferencesSection =
  | typeof Sections.COURSE_EMAIL_PREFERENCES
  | typeof Sections.PARTNER_EMAIL_PREFERENCES
  | typeof Sections.COURSERA_EMAIL_PREFERENCES;

const APIs = {
  userEmails: API('/api/grpc/useremails/v1/UserEmailsAPI', { type: 'rest' }),
  get two_factor_authentication() {
    return API('/api/2fa/v2', { type: 'rest' });
  },
  email_preferences: API('/api/notificationPreferences.v2/', { type: 'rest' }),
  banner_view_status: API('/api/onDemandTutorialViews.v1/', { type: 'rest' }),
  password: API('/api/password/v1/change', { type: 'rest' }),
  wallet: API('/api/paymentWallets.v1', { type: 'rest' }),
  account_deletion: API('/api/userAccounts.v1', { type: 'rest' }),
  name_change_request: API('maestro/api/credentials'),
  request_reset_password: API('/api/password/v2/requestReset', { type: 'rest' }),
};

const BANNER_DISMISS_PREFIX = 'partnerBannerDismissed';
const EMAIL_IN_USE_ERROR = 'Email is already used by another account.';

function formatPaymentMethodToWallet(paymentMethod: PaymentMethodDetailsFragment): PaymentWallet {
  const isCard = paymentMethod.details?.__typename === 'Payments_CardPaymentMethodDetails';
  const cardDetails =
    isCard && paymentMethod.details?.__typename === 'Payments_CardPaymentMethodDetails' ? paymentMethod.details : null;

  return {
    id: parseInt(paymentMethod.id, 10),
    userId: parseInt(paymentMethod.user.id, 10),
    billingCountry: paymentMethod.billingInfo?.countryIsoCode || '',
    billingZipcode: paymentMethod.billingInfo?.postalCode || '',
    creditCardExpiration:
      cardDetails?.expiryYear && cardDetails?.expiryMonth
        ? new Date(cardDetails.expiryYear, cardDetails.expiryMonth - 1).getTime()
        : undefined,
    creditCardLastFourDigits: cardDetails?.last4 || '',
    creditCardType: cardDetails?.brandType || '',
    paymentProcessorId: paymentMethod.paymentProcessorId,
    walletType: cardDetails?.walletType || '',
  };
}

const SettingsAPIUtils = {
  /**
   * Get current user settings
   * User and EmailPreferences needs to be fetched sequentially
   * And then courses and partners data can be fetched simultaneously
   * After all is done user data and email setting preferences are ready
   * @return {Object}
   */
  getUserSettings(userId: number, client: ApolloClient<unknown>) {
    return Q().then(() => {
      SettingsAPIUtils.fetchCreditCardPreferences(userId);
      SettingsAPIUtils.fetchPaymentWallets(userId, client);
      SettingsAPIUtils.fetchEmailPreferences(userId, client);
    });
  },

  /**
   * CREDIT CARD
   */

  fetchCreditCardPreferences(userId: number) {
    return SettingsAPIUtils.getCreditCardPreferences(userId).then((res) => {
      SettingsServerActionCreators.receivedCreditCardPreferences(res.elements[0]);
    });
  },

  fetchPaymentWallets(userId: number, client?: ApolloClient<unknown>) {
    const getWalletsPromise = SettingsAPIUtils.getPaymentWallets(userId);
    const getPaymentMethodsPromise = SettingsAPIUtils.getGraphQLPaymentMethods(client);

    return Promise.all([getWalletsPromise, getPaymentMethodsPromise])
      .then(([walletsRes, paymentMethods]) => {
        const wallets = walletsRes.elements.filter((wallet: PaymentWalletsV1) =>
          [PaymentProcessorTypes.braintree, PaymentProcessorTypes.paypal].includes(wallet.paymentProcessorId || '')
        );
        const formattedPaymentMethods = paymentMethods.map(formatPaymentMethodToWallet);
        SettingsServerActionCreators.receivedPaymentWallets([...wallets, ...formattedPaymentMethods]);
      })
      .catch((error) => {
        console.error('Error fetching payment wallets:', error);
      });
  },

  getPaymentWallets(userId: number) {
    const uri = new URI();
    uri.addQueryParam('q', 'me');
    uri.addQueryParam('userId', userId);
    uri.addQueryParam('getLatest', 'true');
    uri.addQueryParam(
      'fields',
      'billingCountry,billingZipcode,creditCardExpiration,creditCardLastFourDigits,creditCardType,isValid,paymentProcessorId,email'
    );
    return Q(APIs[Fields.WALLET].get(uri.toString()));
  },

  getGraphQLPaymentMethods(client?: ApolloClient<unknown>) {
    if (!client) {
      return Promise.resolve([]);
    }

    return client
      .query<GetPaymentMethodsByPaymentProcessorIdsQuery, GetPaymentMethodsByPaymentProcessorIdsQueryVariables>({
        query: GetPaymentMethodsByPaymentProcessorIds,
        variables: {
          paymentProcessorIds: STRIPE_CONSUMER_PAYMENT_PROCESSOR_IDS,
        },
        context: { clientName: 'gatewayGql' },
        fetchPolicy: 'network-only',
      })
      .then((result) => result.data?.PaymentMethods?.findPaymentMethods?.elements || [])
      .catch(() => Promise.resolve([]));
  },

  /**
   * Get user credit card preferences
   * @return {Object}
   */
  getCreditCardPreferences(userId: number) {
    const uri = new URI();
    uri.addQueryParam('q', 'findByUser');
    uri.addQueryParam('userId', userId);
    uri.addQueryParam('getLatest', 'true');
    uri.addQueryParam('fields', 'creditCardExpiration,creditCardLastFourDigits,creditCardType,isValid');

    return Q(APIs[Fields.WALLET].get(uri.toString()));
  },

  /**
   * Get user credit card preferences
   * @param {Number} walletId
   * @return {Object}
   */
  deleteCreditCard(walletId: $TSFixMe) {
    SettingsServerActionCreators.updateStarted(Sections.CREDIT_CARD_DELETE);

    const uri = new URI(walletId);

    return Q(APIs[Fields.WALLET].delete(uri.toString()))
      .then((data) => {
        SettingsServerActionCreators.updateSucceeded(Sections.CREDIT_CARD_DELETE, data);
      })
      .catch(() => {
        SettingsServerActionCreators.updateFailed(Sections.CREDIT_CARD_DELETE);
      });
  },

  /**
   * EMAILS
   */

  fetchEmailPreferences(userId: number, client: ApolloClient<unknown>) {
    SettingsAPIUtils.getEmailPreferences(userId).then((response) => {
      const notificationPref = response.elements[0];
      const coursePreferences = notificationPref.courses.preferences;

      let courseIds = _.keys(coursePreferences);

      // help desk degrees and certificates students enrollment has to be filtered out
      courseIds = courseIds.filter((id) => id !== 'xlDT6ls8EeaFYA4dzi3AFw');

      const partnerIds = _.keys(notificationPref.partners.preferences);

      if (_.isEmpty(partnerIds)) {
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 3 arguments, but got 1.
        const mergedPreferences = SettingsAPIUtils.mergePreferences(notificationPref);
        SettingsServerActionCreators.receivedEmailPreferences(notificationPref, mergedPreferences);
      } else {
        const coursePromises = SettingsAPIUtils.getCoursePromises(courseIds, client);

        Q.all(coursePromises).then((data) => {
          let coursesData: $TSFixMe = [];
          _.map(data, (cd) => {
            coursesData.push(cd?.Course?.queryByIds);
          });
          coursesData = _.flattenDeep(coursesData);
          const mergedPref = SettingsAPIUtils.mergePreferences(notificationPref, coursesData);
          SettingsServerActionCreators.receivedEmailPreferences(notificationPref, mergedPref);
        });
      }
    });
  },

  /**
   * Updates email preferences
   * @param {String} section
   * @param {Object} preferences
   * @return {Object}
   */
  updateEmailPreferences(section: $TSFixMe, preferences: $TSFixMe, client: ApolloClient<unknown>) {
    const userId = user.get().id;
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'number' is not assignable to par... Remove this comment to see the full error message
    const uri = new URI(userId);
    const preparedPreferences = this.preparePreferences(section, preferences);
    SettingsServerActionCreators.updateStarted(section);

    return Q(
      APIs[Fields.EMAIL_PREFERENCES].put(uri.toString(), {
        data: preparedPreferences,
      })
    )
      .then(() => {
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 2 arguments, but got 1.
        SettingsServerActionCreators.updateSucceeded(section);
      })
      .catch(() => {
        SettingsServerActionCreators.updateFailed(section);
      })
      .done(() => {
        SettingsAPIUtils.fetchEmailPreferences(userId, client);
      });
  },

  getEmailPreferences(userId: number) {
    const uri = new URI(String(userId));
    uri.addQueryParam('fields', 'volunteer,reminders');

    return Q(APIs[Fields.EMAIL_PREFERENCES].get(uri.toString()));
  },

  /**
   * Unsubscribe from all emails by explicitly changing all emails preferences to UNSUBSCRIBED
   */
  unsubscribeAllEmails() {
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'number' is not assignable to par... Remove this comment to see the full error message
    const uri = new URI(user.get().id);
    const notificationPreferences = SettingsStore.getNotificationPreferences();
    SettingsServerActionCreators.updateStarted(Sections.GLOBAL_EMAIL_PREFERENCES);

    notificationPreferences.global = Subscriptions.DISABLED;
    notificationPreferences.promotions.email = Subscriptions.UNSUBSCRIBED;
    notificationPreferences.recommendations.email = Subscriptions.UNSUBSCRIBED;
    notificationPreferences.volunteer.email = Subscriptions.UNSUBSCRIBED;
    if (notificationPreferences.reminders) {
      notificationPreferences.reminders.email = Subscriptions.UNSUBSCRIBED;
    }

    _.forEach(notificationPreferences.partners.preferences, (partnerPreference) => {
      partnerPreference.subscriptions.email = Subscriptions.UNSUBSCRIBED;
    });

    _.forEach(notificationPreferences.courses.preferences, (partnerPreference) => {
      partnerPreference.announcements.email = Subscriptions.UNSUBSCRIBED;
      if (partnerPreference.reminders) {
        partnerPreference.reminders.email = Subscriptions.UNSUBSCRIBED;
      }
    });

    return Q(
      APIs[Fields.EMAIL_PREFERENCES].put(uri.toString(), {
        data: notificationPreferences,
      })
    )
      .then(() => {
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 2 arguments, but got 1.
        SettingsServerActionCreators.updateSucceeded(Sections.GLOBAL_EMAIL_PREFERENCES);
        SettingsServerActionCreators.unsubscribedAllEmails();
      })
      .catch(() => {
        SettingsServerActionCreators.updateFailed(Sections.GLOBAL_EMAIL_PREFERENCES);
      })
      .done(() => {
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 0.
        this.getUserSettings();
      });
  },

  togglePartnerEmailSubscription(
    partnerId: $TSFixMe,
    courseId: $TSFixMe,
    subscribe: $TSFixMe,
    client: ApolloClient<unknown>
  ) {
    const preference = {};
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    preference[partnerId] = {
      id: partnerId,
      value: {
        subscriptions: subscribe,
      },
    };
    const subscription = subscribe ? PartnerBannerStatus.SUBSCRIBED : PartnerBannerStatus.UNSUBSCRIBED;
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'done' does not exist on type 'void'.
    SettingsAPIUtils.updateEmailPreferences(Sections.PARTNER_EMAIL_PREFERENCES, preference, client).done(() => {
      SettingsServerActionCreators.partnerBannerStatusReceived(partnerId, subscription);
      this.dismissPartnerBanner(courseId, partnerId, true);
    });
  },

  /**
   * Check partner marketing email status (Dismissed, Subscribed or Waiting)
   * If not dismissed will check if user is already subscribed to partner emails
   * Otherwise the status is Waiting meaning banner will show up to ask user to subscribe or dismiss
   * @param courseId
   * @param partnerId
   */
  checkPartnerBannerStatus(courseId: $TSFixMe, partnerId: $TSFixMe) {
    const userId = user.get().id;
    const uri = new URI(`${userId}~${BANNER_DISMISS_PREFIX}-${courseId}-${partnerId}`);

    return Q(APIs.banner_view_status.get(uri.toString()))
      .then(() => {
        SettingsServerActionCreators.partnerBannerStatusReceived(partnerId, PartnerBannerStatus.DISMISSED);
      })
      .catch(() => {
        this.getEmailPreferences(userId).then((response) => {
          const preferences = response.elements[0];
          const partnerPreference = preferences.partners.preferences[partnerId];
          let subscriptionStatus: (typeof PartnerBannerStatus)[keyof typeof PartnerBannerStatus] =
            PartnerBannerStatus.SUBSCRIBED;

          if (partnerPreference && partnerPreference.subscriptions.email === Subscriptions.UNSUBSCRIBED) {
            subscriptionStatus = PartnerBannerStatus.UNSUBSCRIBED;
          }

          SettingsServerActionCreators.receivedEmailPreferences(preferences, null);
          SettingsServerActionCreators.partnerBannerStatusReceived(partnerId, subscriptionStatus);
        });
      });
  },

  dismissPartnerBanner(courseId: $TSFixMe, partnerId: $TSFixMe, quiet: $TSFixMe) {
    const userId = user.get().id;
    const uri = new URI(`${userId}~${BANNER_DISMISS_PREFIX}-${courseId}-${partnerId}`);
    return Q(APIs.banner_view_status.put(uri.toString())).done(() => {
      if (!quiet) {
        SettingsServerActionCreators.partnerBannerStatusReceived(partnerId, PartnerBannerStatus.DISMISSED);
      }
    });
  },

  /**
   * PASSWORD
   */

  /**
   * Updates account password
   * @param {Object} passwords - current and newPassword
   * @return {Object}
   */
  resetPassword(passwords: $TSFixMe) {
    SettingsServerActionCreators.updateStarted(Sections.PASSWORD);
    if (passwords.newPassword !== passwords.retypedPassword) {
      SettingsServerActionCreators.updateFieldFailed(Fields.NEW_PASSWORD);
      SettingsServerActionCreators.updateFailed(Sections.PASSWORD);
    } else {
      const formData = new FormData();
      formData.append('currentPassword', passwords.currentPassword);
      formData.append('newPassword', passwords.newPassword);

      return Q(
        APIs.password.post('', {
          // @ts-expect-error ts-migrate(2322) FIXME: Type 'false' is not assignable to type 'string | u... Remove this comment to see the full error message
          contentType: false,
          processData: false,
          data: formData,
        })
      )
        .then((data) => {
          SettingsServerActionCreators.updateSucceeded(Sections.PASSWORD, data);
        })
        .catch((error) => {
          if (error.responseText === 'Password is not sufficiently complex') {
            SettingsServerActionCreators.updatePasswordFailedInsufficientlyComplex(Sections.PASSWORD);
          } else if (error.responseText === 'Incorrect existing password') {
            SettingsServerActionCreators.updateFieldFailed(Fields.PASSWORD);
            SettingsServerActionCreators.updateFailed(Sections.PASSWORD);
          } else {
            SettingsServerActionCreators.updateFailed(Sections.PASSWORD);
          }
        });
    }
  },

  requestResetPassword() {
    SettingsServerActionCreators.updateStarted(Sections.PERSONAL_ACCOUNT);
    const data = { email: user.get().email_address };

    return Q(APIs.request_reset_password.post('', { data }))
      .then((response) => {
        SettingsServerActionCreators.updateSucceeded(Sections.PERSONAL_ACCOUNT, response);
      })
      .catch(() => {
        SettingsServerActionCreators.updateFailed(Sections.PERSONAL_ACCOUNT);
      });
  },

  sendVerificationEmail(email: string) {
    SettingsServerActionCreators.updateStarted(Sections.PERSONAL_ACCOUNT);
    const data = {
      emailAddress: email,
      userId: Number(user.get().id),
    };

    return Q(
      APIs.userEmails.post('SendVerificationEmail', {
        data,
      })
    )
      .then((response) => {
        SettingsServerActionCreators.updateSucceeded(Sections.PERSONAL_ACCOUNT, response);
      })
      .catch(() => {
        SettingsServerActionCreators.updateFailed(Sections.PERSONAL_ACCOUNT);
      });
  },

  addAlternativeEmail(email: string) {
    SettingsServerActionCreators.updateStarted(Sections.PERSONAL_ACCOUNT);
    const data = {
      isPrimary: false,
      isVerified: false,
      emailAddress: email,
      userId: Number(user.get().id),
    };

    return Q(
      APIs.userEmails.post('InsertUserEmail', {
        data,
      })
    )
      .then((response) => {
        SettingsServerActionCreators.updateSucceeded(Sections.PERSONAL_ACCOUNT, response);
      })
      .catch((error) => {
        if (error.responseJSON && error.responseJSON.message.includes('already exists')) {
          SettingsServerActionCreators.updateFailedEmailInUse(Sections.PERSONAL_ACCOUNT);
        } else {
          SettingsServerActionCreators.updateFailed(Sections.PERSONAL_ACCOUNT);
          SettingsServerActionCreators.updateFieldFailed(Fields.ALTERNATIVE_EMAIL);
        }
        return error;
      });
  },

  /**
   * Verifies user's password and disables Two-Factor Authentication
   */
  confirmPasswordToDisableTwoFactorAuthentication(password: $TSFixMe) {
    // HACK to get around not being able to dispatch in the middle of a dispatch
    setTimeout(() => SettingsServerActionCreators.beginTwoFactorAuthenticationServerAction());
    const uri = new URI('/disable');

    return Q(
      APIs.two_factor_authentication.post(uri.toString(), {
        data: { password },
      })
    )
      .then(() => SettingsServerActionCreators.receivedSuccessfulDisablingTwoFactorAuthenticationResponse())
      .catch(() => SettingsServerActionCreators.receivedInvalidPassword())
      .done();
  },

  /**
   * TWO FACTOR
   */

  /**
   * Loads Two-Factor Authentication settings from the server
   */
  loadTwoFactorAuthenticationSettings() {
    // HACK to get around not being able to dispatch in the middle of a dispatch
    setTimeout(() => SettingsServerActionCreators.beganLoadingTwoFactorAuthenticationSettings());
    const uri = new URI('/enabled');

    return Q(APIs.two_factor_authentication.get(uri.toString()))
      .then((settings) => SettingsServerActionCreators.receivedTwoFactorAuthenticationSettings(settings))
      .done();
  },

  /**
   * Verifies user's password and receives QR code to continue Two-Factor Authentication setup process
   */
  confirmPasswordToEnableTwoFactorAuthentication(password: $TSFixMe) {
    // HACK to get around not being able to dispatch in the middle of a dispatch
    setTimeout(() => SettingsServerActionCreators.beginTwoFactorAuthenticationServerAction());
    const uri = new URI('/create');

    return Q(
      APIs.two_factor_authentication.post(uri.toString(), {
        data: { password },
      })
    )
      .then((res) => SettingsServerActionCreators.receivedTwoFactorQRCode(res.details.imgSrc))
      .catch(() => SettingsServerActionCreators.receivedInvalidPassword())
      .done();
  },

  /**
   * Verifies user's password and receives QR code to continue Two-Factor Authentication setup process
   */
  confirmTwoFactorAuthenticationCode(code: $TSFixMe) {
    // HACK to get around not being able to dispatch in the middle of a dispatch
    setTimeout(() => SettingsServerActionCreators.beginTwoFactorAuthenticationServerAction());
    const uri = new URI('/enable');

    return Q(APIs.two_factor_authentication.post(uri.toString(), { data: { code } }))
      .then(() => SettingsServerActionCreators.receivedConfirmTwoFactorAuthenticationCodeResponse(true))
      .catch(() => SettingsServerActionCreators.receivedConfirmTwoFactorAuthenticationCodeResponse(false))
      .done();
  },

  /**
   * DATA MANIPULATION
   */

  /**
   * Prepares preferences request body
   * @param {String} type
   * @param {Object} preferences
   * @return {Object}
   */
  preparePreferences(
    type: NotificationPreferencesSection,
    preferences: EmailPreference[],
    channel: NotificationPreferenceChannel = NotificationPreferenceChannels.EMAIL
  ) {
    const notificationPreferences = SettingsStore.getNotificationPreferences() || {};
    let emailPreferences: $TSFixMe;

    if (channel === NotificationPreferenceChannels.EMAIL) {
      // Re-enable global email subscription when any email pref is changed
      notificationPreferences.global = Subscriptions.ENABLED;
    }

    switch (type) {
      case Sections.COURSE_EMAIL_PREFERENCES:
        emailPreferences = notificationPreferences.courses ? notificationPreferences.courses.preferences : {};
        break;
      case Sections.PARTNER_EMAIL_PREFERENCES:
        emailPreferences = notificationPreferences.partners ? notificationPreferences.partners.preferences : {};
        break;
      case Sections.COURSERA_EMAIL_PREFERENCES:
        emailPreferences = notificationPreferences;
        break;
    }

    _.forEach(preferences, (preference) => {
      _.forEach(preference.value, (value, key) => {
        const subscription = value ? Subscriptions.SUBSCRIBED : Subscriptions.UNSUBSCRIBED;
        if (type === Sections.COURSERA_EMAIL_PREFERENCES) {
          if (preference.id === 'degrees') {
            if (!emailPreferences[preference.id] || !emailPreferences[preference.id].preference)
              emailPreferences[preference.id] = { preferences: {} };
            emailPreferences[preference.id].degree_eoi = subscription;
          } else {
            if (!emailPreferences[preference.id]) emailPreferences[preference.id] = {};
            emailPreferences[preference.id][channel] = subscription;
          }
        } else if (emailPreferences[preference.id]) {
          emailPreferences[preference.id][key].email = subscription;
        } else {
          emailPreferences[preference.id] = {
            [key]: {
              email: subscription,
            },
          };
        }
      });
    });

    return notificationPreferences;
  },

  /**
   * Updates a field
   * @param {String} type
   * @param {String} value
   * @return {Object}
   */
  update(type: $TSFixMe, value: $TSFixMe) {
    return Q(
      // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
      APIs[type].post('', {
        contentType: 'application/x-www-form-urlencoded',
        processData: true,
        data: { [type]: value },
      })
    ).catch((error) => {
      // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 2.
      SettingsServerActionCreators.updateFieldFailed(type, error);
      return { type, error };
    });
  },

  /**
   * Merges course and partners data to email preferences
   * @param {Array} preferences
   * @param {Array} courses
   * @return {Object}
   */
  mergePreferences(preferences: $TSFixMe, courses: $TSFixMe) {
    const mergedPreferences = {
      courses: [],
      partners: [],
      coursera: {
        promotions: {
          email: preferences.promotions?.email === Subscriptions.SUBSCRIBED,
          push: preferences.promotions?.push === Subscriptions.SUBSCRIBED,
        },
        recommendations: {
          email: preferences.recommendations?.email === Subscriptions.SUBSCRIBED,
        },
        volunteer: {
          email: preferences.volunteer?.email === Subscriptions.SUBSCRIBED,
        },
        degrees: {
          email: preferences.degrees?.degree_eoi === Subscriptions.SUBSCRIBED,
        },
        reminders: {
          email: preferences.reminders?.email === Subscriptions.SUBSCRIBED,
        },
      },
    };

    _.map(preferences.courses.preferences, function (preference, courseId) {
      const foundCourses = _.filter(courses, { id: courseId });
      if (!_.isEmpty(foundCourses)) {
        const course = foundCourses[0];
        const mergedCourse = {
          id: courseId,
          value: {
            announcements: preference.announcements.email === Subscriptions.SUBSCRIBED,
          },
          name: course.name,
        };
        if (course.promoPhotoUrl) {
          const logo = `https://d3njjcbhbojbot.cloudfront.net/api/utilities/v1/imageproxy/${course.promoPhotoUrl}`;
          // @ts-expect-error ts-migrate(2339) FIXME: Property 'logo' does not exist on type '{ id: stri... Remove this comment to see the full error message
          mergedCourse.logo = `${logo}?h=${EMAIL_PREFERENCE_LOGO_SIZE}`;
        }
        if (course.partners) {
          // @ts-expect-error ts-migrate(2339) FIXME: Property 'description' does not exist on type '{ i... Remove this comment to see the full error message
          mergedCourse.description = course.partners[0] && course.partners[0].name;
        }
        if (preference.reminders) {
          // @ts-expect-error ts-migrate(2339) FIXME: Property 'reminders' does not exist on type '{ ann... Remove this comment to see the full error message
          mergedCourse.value.reminders = preference.reminders.email === Subscriptions.SUBSCRIBED;
        }
        if (preference.discussionForums) {
          // @ts-expect-error ts-migrate(2339) FIXME: Property 'discussionForums' does not exist on type... Remove this comment to see the full error message
          mergedCourse.value.discussionForums = preference.discussionForums.email === Subscriptions.SUBSCRIBED;
        }
        // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '{ id: string; value: { announcem... Remove this comment to see the full error message
        mergedPreferences.courses.push(mergedCourse);
      }
    });

    // @ts-expect-error ts-migrate(2322) FIXME: Type 'any[]' is not assignable to type 'never[]'.
    mergedPreferences.courses = this.groupCourses(mergedPreferences.courses);

    _.map(preferences.partners.preferences, function (preference, partnerId) {
      const partners = courses.reduce((acc: $TSFixMe, course: $TSFixMe) => acc.concat(course.partners), []);
      const foundPartners = _.filter(partners, { id: partnerId });
      if (!_.isEmpty(foundPartners)) {
        const partner = foundPartners[0];
        const mergedPartner = {
          id: partnerId,
          value: {
            subscriptions: preference.subscriptions.email === Subscriptions.SUBSCRIBED,
          },
          name: partner.name,
        };
        if (partner.squareLogo) {
          // @ts-expect-error ts-migrate(2339) FIXME: Property 'logo' does not exist on type '{ id: stri... Remove this comment to see the full error message
          mergedPartner.logo = partner.squareLogo;
        }
        // @ts-expect-error ts-migrate(2345) FIXME: Argument of type '{ id: string; value: { subscript... Remove this comment to see the full error message
        mergedPreferences.partners.push(mergedPartner);
      }
    });

    return mergedPreferences;
  },

  /**
   * Get all courses information by id
   * @param {Array} courseIds
   * @return {Object}
   */
  getCourses(courseIds: $TSFixMe, client: ApolloClient<unknown>) {
    if (_.isEmpty(courseIds)) {
      return Promise.resolve();
    } else {
      return client
        .query({
          query: GetCoursesForEmailPreferencesQuery,
          variables: { ids: courseIds },
          context: {
            clientName: 'gatewayGql',
          },
        })
        .then(({ data }) => {
          return data;
        })
        .catch(() => {
          return Promise.resolve();
        });
    }
  },

  /**
   * Returns promises for getting courses for the user
   * @param {Array} courseIds
   * @return {Object}
   */
  getCoursePromises(courseIds: $TSFixMe, client: ApolloClient<unknown>) {
    const onDemandCoursePromises = [];
    const v1SessionCoursePromises = [];
    const onDemandCourseIds = courseIds.filter((id: $TSFixMe) => !/^v1/.test(id));
    const v1SessionCourseIds = courseIds.filter((id: $TSFixMe) => /^v1/.test(id));

    const idLimit = 70; // We cross the URI length limit at 79 ids

    for (let i = 0; i < onDemandCourseIds.length; i += idLimit) {
      onDemandCoursePromises.push(SettingsAPIUtils.getCourses(onDemandCourseIds.slice(i, i + idLimit), client));
    }

    for (let i = 0; i < v1SessionCourseIds.length; i += idLimit) {
      v1SessionCoursePromises.push(SettingsAPIUtils.getCourses(v1SessionCourseIds.slice(i, i + idLimit), client));
    }

    return [...onDemandCoursePromises, ...v1SessionCoursePromises];
  },

  /**
   * Groups phoenix and spark courses in two lists
   * Sorts each array by course name and flatten the sorted result as a list
   * @param courses
   * @return {Array} of courses
   */
  groupCourses(courses: $TSFixMe) {
    const groupedCourses = _.groupBy(courses, (course) => course.id.indexOf('v1-') === 0);
    const phoenixCourses = this.sortCoursesByName(groupedCourses.false);
    const sparkCourses = this.sortCoursesByName(groupedCourses.true);

    return phoenixCourses.concat(sparkCourses);
  },

  /**
   * Sorts courses by course name
   * @param courses
   * @return {Array} of courses
   */
  sortCoursesByName(courses: $TSFixMe) {
    return _.sortBy(courses, (course) => course.name);
  },

  /**
   * Updates email address using password
   * @param {String} email
   * @param {String} password
   * @param {Number} userId
   */
  updateEmailAddressWithPassword(email: string, password: string, userId: number) {
    return Q(
      APIs.userEmails.post('SetPrimaryEmail', {
        data: {
          emailAddress: email,
          password,
          userId,
          sendVerificationEmail: true,
        },
      })
    )
      .then((data) => {
        SettingsServerActionCreators.updatedEmailAddress(email);
        SettingsServerActionCreators.updateSucceeded(Sections.BASIC_INFORMATION, data);
      })
      .catch((error) => {
        if (error.responseJSON && error.responseJSON?.message?.includes(EMAIL_IN_USE_ERROR)) {
          SettingsServerActionCreators.updateFailedEmailInUse(Sections.BASIC_INFORMATION);
        } else {
          throw error;
        }
      });
  },

  /**
   * Submits name change request
   * @param {Object} data
   */
  submitNameChangeRequest(nameChangeData: $TSFixMe) {
    const uri = new URI('/rename_profile');
    return Q(APIs.name_change_request.post(uri.toString(), { data: nameChangeData }));
  },

  logout() {
    // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'string | null' is not assignable... Remove this comment to see the full error message
    const logoutAPI = API(getActionUrl('logout', cookie.get('CSRF3-Token')));
    return (
      Q(
        logoutAPI.post('', {
          contentType: 'application/x-www-form-urlencoded',
          processData: true,
        })
      )
        .then(() => {
          SettingsServerActionCreators.accountDeleted();
        })
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 1 arguments, but got 0.
        .catch()
        .done()
    );
  },
};

export default SettingsAPIUtils;

export const {
  getUserSettings,
  fetchCreditCardPreferences,
  fetchPaymentWallets,
  getCreditCardPreferences,
  deleteCreditCard,
  fetchEmailPreferences,
  updateEmailPreferences,
  getEmailPreferences,
  unsubscribeAllEmails,
  togglePartnerEmailSubscription,
  checkPartnerBannerStatus,
  dismissPartnerBanner,
  resetPassword,
  requestResetPassword,
  addAlternativeEmail,
  confirmPasswordToDisableTwoFactorAuthentication,
  loadTwoFactorAuthenticationSettings,
  confirmPasswordToEnableTwoFactorAuthentication,
  confirmTwoFactorAuthenticationCode,
  preparePreferences,
  update,
  mergePreferences,
  getCourses,
  groupCourses,
  sortCoursesByName,
  updateEmailAddressWithPassword,
  submitNameChangeRequest,
  logout,
} = SettingsAPIUtils;
